<template>
    <div
        class="app-toast"
        :class="[`app-toast--${kind}`, { 'app-toast--single-line': isSingleLine, 'app-toast--timed': lifeMs > 0 }]"
        :role="isUrgent ? 'alert' : 'status'"
        :aria-live="isUrgent ? 'assertive' : 'polite'"
    >
        <div class="app-toast__icon" aria-hidden="true">
            <CheckCircleFilled v-if="kind === 'success'" />
            <CloseCircleFilled v-else-if="kind === 'error'" />
            <ExclamationCircleFilled v-else-if="kind === 'warning'" />
            <InfoCircleFilled v-else />
        </div>

        <div class="app-toast__body">
            <p v-if="item.title" class="app-toast__title">{{ item.title }}</p>
            <p v-if="item.text" class="app-toast__text">{{ item.text }}</p>
        </div>

        <button
            type="button"
            class="app-toast__close"
            aria-label="Dismiss notification"
            @click.stop="close"
        >
            <CloseOutlined />
        </button>

        <div v-if="lifeMs > 0" class="app-toast__progress" aria-hidden="true">
            <span class="app-toast__progress-fill" :style="{ animationDuration: `${lifeMs}ms` }"></span>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled,
    InfoCircleFilled,
    CloseOutlined
} from '@ant-design/icons-vue';
import type { NotificationItem } from '@kyvg/vue3-notification';
import type { ToastType } from '@/composables/useToast';

/**
 * Visual for a single toast. Rendered through the `body` slot of the
 * `<notifications>` outlet in App.vue, so the library still owns the
 * stacking, timers, and enter/leave transitions.
 */
export default defineComponent({
    name: 'AppToast',
    components: {
        CheckCircleFilled,
        CloseCircleFilled,
        ExclamationCircleFilled,
        InfoCircleFilled,
        CloseOutlined
    },
    props: {
        item: {
            type: Object as PropType<NotificationItem>,
            required: true
        },
        close: {
            type: Function as PropType<() => void>,
            required: true
        }
    },
    setup(props) {
        const kind = computed<ToastType>(() => {
            const t = props.item.type;
            if (t === 'success' || t === 'error' || t === 'warning') return t;
            if (t === 'warn') return 'warning';
            return 'info';
        });

        const isUrgent = computed(() => kind.value === 'error' || kind.value === 'warning');

        // A toast with only a title (or only text) is one short row, so centre it
        // against the icon chip instead of hanging it off the top edge.
        const isSingleLine = computed(() => !props.item.title || !props.item.text);

        // `length` is the total on-screen time the library calculated for this
        // item (duration plus the enter/leave animation). Drives the progress bar.
        const lifeMs = computed(() => (props.item.length && props.item.length > 0 ? props.item.length : 0));

        return { kind, isUrgent, isSingleLine, lifeMs };
    }
});
</script>

<style lang="scss" scoped>
$toast-success: #34c759;
$toast-error: #ff3b30;
$toast-warning: #ff9500;
$toast-info: #007aff;

.app-toast {
    --toast-accent: #{$toast-info};
    --toast-tint: rgba(0, 122, 255, 0.12);

    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin: 0 0 10px 0;
    padding: 13px 12px 14px 18px;
    overflow: hidden;
    cursor: default;

    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    color: #1d1d1f;

    // Glass card with a faint wash of the status colour bleeding in from the accent edge.
    background:
        linear-gradient(90deg, var(--toast-tint) 0%, rgba(255, 255, 255, 0) 38%),
        rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 14px;
    box-shadow:
        inset 4px 0 0 0 var(--toast-accent),
        0 1px 2px rgba(0, 0, 0, 0.04),
        0 10px 30px rgba(0, 0, 0, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.85);

    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        box-shadow:
            inset 4px 0 0 0 var(--toast-accent),
            0 2px 4px rgba(0, 0, 0, 0.05),
            0 14px 36px rgba(0, 0, 0, 0.16),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
    }


    &--success { --toast-accent: #{$toast-success}; --toast-tint: rgba(52, 199, 89, 0.12); }
    &--error   { --toast-accent: #{$toast-error};   --toast-tint: rgba(255, 59, 48, 0.12); }
    &--warning { --toast-accent: #{$toast-warning}; --toast-tint: rgba(255, 149, 0, 0.14); }
    &--info    { --toast-accent: #{$toast-info};    --toast-tint: rgba(0, 122, 255, 0.12); }
}

// Single-row toasts line the title up with the middle of the icon chip.
.app-toast--single-line {
    align-items: center;

    .app-toast__body {
        padding-top: 0;
    }

    .app-toast__close {
        margin-top: 0;
    }
}

// Icon sits in a tinted chip so it reads as a badge rather than a loose glyph.
.app-toast__icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: var(--toast-tint);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.5);
    font-size: 18px;
    line-height: 1;
    color: var(--toast-accent);
}

.app-toast__body {
    flex: 1 1 auto;
    min-width: 0;
    padding-top: 1px;
}

.app-toast__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.35;
    color: #1d1d1f;
}

.app-toast__text {
    margin: 2px 0 0 0;
    font-size: 13px;
    font-weight: 400;
    letter-spacing: -0.01em;
    line-height: 1.45;
    color: #6e6e73;
    overflow-wrap: anywhere;
}

// Close control stays quiet until the toast is hovered or focused.
.app-toast__close {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    margin: -2px -2px 0 0;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #86868b;
    font-size: 11px;
    opacity: 0.45;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease, transform 0.15s ease;

    .app-toast:hover &,
    .app-toast:focus-within & {
        opacity: 1;
    }

    &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #1d1d1f;
    }

    &:active {
        transform: scale(0.92);
    }

    &:focus-visible {
        opacity: 1;
        outline: 2px solid #007aff;
        outline-offset: 1px;
    }
}

// Remaining-time indicator: a hairline pill on a faint track, tucked inside the
// bottom padding so it reads as a timer rather than a border. The fill's width
// drains left to right, so its rounded ends never distort.
//
// The fill is a real element (not a pseudo) and its animation-duration is set
// inline from the template rather than via a custom property: some engines do
// not reliably resolve var() into pseudo-element animations, which left the
// bar frozen at empty.
.app-toast--timed {
    padding-bottom: 20px;
}

.app-toast__progress {
    position: absolute;
    left: 18px;
    right: 14px;
    bottom: 8px;
    height: 3px;
    border-radius: 999px;
    background: var(--toast-tint);
    overflow: hidden;
}

.app-toast__progress-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    border-radius: inherit;
    // Accent with a light wash toward the leading edge.
    background:
        linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.3) 100%),
        var(--toast-accent);
    animation-name: app-toast-drain;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
}

// Freeze the bar while hovered; the outlet pauses the dismiss timer at the same time.
.app-toast:hover .app-toast__progress-fill {
    animation-play-state: paused;
}

@keyframes app-toast-drain {
    from { width: 100%; }
    to   { width: 0; }
}

@media (prefers-reduced-motion: reduce) {
    .app-toast,
    .app-toast__close {
        transition: none;
    }
    .app-toast:hover {
        transform: none;
    }
    .app-toast__progress-fill {
        animation: none;
    }
}
</style>
